import React, { useState } from 'react';
import { toast } from 'sonner';
import { aiCandidateAPI, errMsg } from '../../../lib/aiInterviewApi';

const OPTIONS = [
  { type: 'extra_time', title: 'Extra time', desc: 'Adds 25% or 50% to the interview length.' },
  { type: 'captions', title: 'Live captions', desc: 'Every question is also shown as text on screen.' },
  { type: 'assistive_technology', title: 'Assistive technology', desc: 'Screen reader, magnifier or other tools. Integrity checks adjust.' },
  { type: 'human_interview', title: 'Human interview instead', desc: 'Ask for an interview with a person from the hiring team.' },
  { type: 'reschedule', title: 'A different time', desc: 'Ask the team for more time before the link expires.' },
  { type: 'other', title: 'Something else', desc: 'Tell us what would help in the note below.' },
];
const LABEL = Object.fromEntries(OPTIONS.map((o) => [o.type, o.title]));
const WAIT_TYPES = ['human_interview', 'reschedule'];

export default function AccommodationStep({ ctx, token, onDone, onRefresh }) {
  const existing = new Set((ctx.accommodations || []).map((a) => a.type));
  const [sel, setSel] = useState(new Set());
  const [percent, setPercent] = useState(25);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentWait, setSentWait] = useState(false);

  const toggle = (t) => setSel((s) => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; });

  const submit = async () => {
    if (!sel.size) return onDone();
    setBusy(true);
    try {
      for (const type of sel) {
        await aiCandidateAPI.accommodation(token, { type, note, ...(type === 'extra_time' ? { percent } : {}) });
      }
      toast.success('Your request was sent to the hiring team');
      await onRefresh();
      if ([...sel].some((t) => WAIT_TYPES.includes(t))) setSentWait(true);
      else onDone();
    } catch (e) {
      toast.error(errMsg(e, 'Could not send your request'));
    } finally {
      setBusy(false);
    }
  };

  if (sentWait) {
    return (
      <section className="iv-card" data-testid="accommodation-sent-card">
        <h1 className="iv-h1">Request sent</h1>
        <p className="iv-sub">The hiring team has been notified and will contact you. You can close this page and come back to the same link later, or continue with the AI interview now if you prefer.</p>
        <div className="iv-row-end">
          <button type="button" className="iv-btn iv-btn-primary" onClick={onDone} data-testid="accommodation-continue-anyway-btn">Continue with the AI interview now</button>
        </div>
      </section>
    );
  }

  return (
    <section className="iv-card" data-testid="candidate-accommodation-card">
      <div className="iv-eyebrow">Optional</div>
      <h1 className="iv-h1">Do you need any adjustments?</h1>
      <p className="iv-sub" style={{ marginBottom: '1rem' }}>Requests are confidential and never affect your evaluation. Skip this step if you don't need anything.</p>
      {existing.size > 0 && (
        <div className="iv-note is-good" style={{ marginBottom: '0.9rem' }} data-testid="accommodation-existing">
          Already requested: {[...existing].map((t) => LABEL[t] || t).join(', ')}
        </div>
      )}
      <div className="iv-options">
        {OPTIONS.map((o) => (
          <button type="button" key={o.type} disabled={existing.has(o.type)} onClick={() => toggle(o.type)}
            className={`iv-option ${sel.has(o.type) ? 'is-on' : ''}`} aria-pressed={sel.has(o.type)} data-testid={`accommodation-option-${o.type}`}>
            <b>{o.title}</b><span>{existing.has(o.type) ? 'Requested' : o.desc}</span>
          </button>
        ))}
      </div>
      {sel.has('extra_time') && (
        <div className="iv-row" style={{ marginTop: '0.9rem' }} data-testid="accommodation-extra-time-options">
          <span className="iv-small">Extra time:</span>
          {[25, 50].map((p) => (
            <button type="button" key={p} className={`iv-chip ${percent === p ? 'is-on' : ''}`} onClick={() => setPercent(p)} data-testid={`accommodation-extra-${p}`}>+{p}%</button>
          ))}
        </div>
      )}
      <label className="iv-small" style={{ display: 'block', marginTop: '1rem' }} htmlFor="acc-note">Note for the hiring team (optional)</label>
      <textarea id="acc-note" className="iv-textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="e.g. I use a screen reader" data-testid="accommodation-note-input" />
      <div className="iv-row-end">
        <button type="button" className="iv-btn iv-btn-cta" disabled={busy} onClick={submit} data-testid="candidate-accommodation-continue-btn">
          {busy ? 'Sending…' : sel.size ? 'Send request & proceed to Tech Check' : 'Proceed to Tech Check'}
        </button>
      </div>
    </section>
  );
}
